import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fmtDate, formatError } from "@/lib/api";
import { Check, X as XIcon, FileDown, ShieldCheck, AlertTriangle, Clock, Building2 } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/18uda418_logo%20Padr%C3%A3o.png";

const statusInfo = (s) => {
  const map = {
    pendente: { label: "Pendente", color: "#FFC107", icon: Clock },
    aprovada: { label: "Aprovada", color: "#1A7F37", icon: Check },
    rejeitada: { label: "Rejeitada", color: "#E60000", icon: XIcon },
    entregue: { label: "Entregue", color: "#5A8F70", icon: Check },
    cancelada: { label: "Cancelada", color: "#555", icon: XIcon },
    devolvida: { label: "Devolvida", color: "#5A8F70", icon: Check },
  };
  return map[s] || map.pendente;
};

export default function RequisitionApprove() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | error | done
  const [error, setError] = useState("");
  const [nome, setNome] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'reject' | null

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/requisitions/${token}`);
      setData(data);
      setPhase("ready");
    } catch (e) {
      setError(e.response?.data?.detail || "Link inválido ou expirado");
      setPhase("error");
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = async (action) => {
    if (!nome.trim()) { toast.error("Informe seu nome"); return; }
    if (action === "reject" && !observacoes.trim()) { toast.error("Informe o motivo da rejeição"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/requisitions/${token}/${action}`, {
        nome_aprovador: nome.trim(),
        observacoes: observacoes.trim(),
      });
      toast.success(action === "approve" ? "Requisição APROVADA · obrigado!" : "Requisição rejeitada");
      setConfirmAction(null);
      await fetchData();
      setPhase("done");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-[#555]">Carregando requisição...</div>;
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
        <div className="brutal-card p-8 max-w-md text-center bg-white">
          <AlertTriangle className="w-12 h-12 text-[#E60000] mx-auto mb-3" />
          <h2 className="font-display font-black text-xl uppercase mb-2">Link inválido</h2>
          <p className="text-sm text-[#555]">{error}</p>
        </div>
      </div>
    );
  }

  const st = statusInfo(data.status);
  const isPending = data.status === "pendente";
  const StIcon = st.icon;
  const pdfUrl = `${API}/public/requisitions/${token}/pdf`;

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-6 px-4" data-testid="req-approve-page">
      <div className="max-w-3xl mx-auto">
        {/* Cabeçalho COOMFLONA */}
        <div className="bg-[#0A1F14] text-white p-5 flex items-center gap-4 mb-1">
          <img src={LOGO_URL} alt="COOMFLONA" className="w-14 h-14 object-contain bg-white/5 p-1" />
          <div className="flex-1">
            <div className="font-display font-black text-xl uppercase tracking-tight leading-none">COOMFLONA</div>
            <div className="font-mono-data text-[10px] opacity-80 mt-1">Cooperativa Mista da Flona Tapajós</div>
            <div className="font-mono-data text-[10px] opacity-80 mt-1">CNPJ 07.315.165/0001-00</div>
          </div>
          <div className="text-right">
            <div className="font-mono-data text-[10px] uppercase tracking-widest opacity-80">Requisição</div>
            <div className="font-display font-black text-2xl">{data.numero_sequencial || data.numero}</div>
          </div>
        </div>

        <div className="brutal-card bg-white p-6 space-y-5">
          {/* Status badge */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-1 font-display font-black uppercase text-xs tracking-widest text-white" style={{ background: st.color }}>
              <StIcon className="w-4 h-4" /> {st.label}
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="req-pdf-link" className="brutal-btn bg-[#3D6B57] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2">
              <FileDown className="w-4 h-4" /> Baixar PDF
            </a>
          </div>

          {/* Dados gerais */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Solicitante</div>
              <div className="font-medium">{data.requester_nome}</div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Setor</div>
              <div className="font-medium">{data.sector_nome || "—"}</div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Criada em</div>
              <div className="font-mono-data text-xs">{fmtDate(data.created_at)}</div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Unidade</div>
              <div className="font-mono-data text-xs flex items-center gap-1"><Building2 className="w-3 h-3" />{data.filial || "—"}</div>
            </div>
          </div>

          {data.motivo && (
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Motivo / Justificativa</div>
              <div className="border-2 border-[#111] p-3 text-sm bg-[#FAFAFA]">{data.motivo}</div>
            </div>
          )}

          {/* Itens */}
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-2">Itens solicitados</div>
            <div className="overflow-x-auto">
              <table className="brutal-table" data-testid="req-items-table">
                <thead><tr><th>Código</th><th>Item</th><th className="text-right">Qtd</th><th className="text-right">Und</th></tr></thead>
                <tbody>
                  {(data.itens || []).map((it, i) => (
                    <tr key={i}>
                      <td className="font-mono-data text-xs">{it.codigo}</td>
                      <td>{it.nome}</td>
                      <td className="text-right font-mono-data font-bold">{it.quantidade_solicitada}</td>
                      <td className="text-right font-mono-data text-xs">{it.unidade || "UN"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Histórico já decidido */}
          {!isPending && (
            <div className="border-2 border-[#111] p-4 bg-[#F0F7F2]">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57] mb-1">Decisão registrada</div>
              <div className="text-sm">
                <strong>{data.aprovador_nome || data.external_approver_name || "—"}</strong>
                {data.external_approved_at && <span className="ml-2 font-mono-data text-xs text-[#555]">em {fmtDate(data.external_approved_at)}</span>}
              </div>
              {data.observacoes_aprovador && <div className="mt-2 text-sm text-[#555]">{data.observacoes_aprovador}</div>}
              {data.external_rejected_reason && <div className="mt-2 text-sm text-[#E60000]">Motivo: {data.external_rejected_reason}</div>}
            </div>
          )}

          {/* Formulário de decisão */}
          {isPending && (
            <div className="border-t-2 border-[#111] pt-4 space-y-3" data-testid="req-decision-form">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FF4F00] flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Aprovação · Setor de Compras
              </div>
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Seu nome *</label>
                <input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Elias da Silva"
                  data-testid="req-aprovador-nome"
                  className="w-full border-2 border-[#111] p-2 text-sm font-mono-data outline-none focus:border-[#FF4F00] bg-white"
                />
              </div>
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Observação / motivo (obrigatório se rejeitar)</label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  data-testid="req-aprovador-obs"
                  className="w-full border-2 border-[#111] p-2 text-sm outline-none focus:border-[#FF4F00] bg-white"
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  disabled={busy}
                  onClick={() => setConfirmAction("approve")}
                  data-testid="req-approve-btn"
                  className="brutal-btn bg-[#1A7F37] text-white px-5 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> Aprovar Requisição
                </button>
                <button
                  disabled={busy}
                  onClick={() => setConfirmAction("reject")}
                  data-testid="req-reject-btn"
                  className="brutal-btn bg-[#E60000] text-white px-5 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <XIcon className="w-4 h-4" /> Rejeitar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center font-mono-data text-[10px] uppercase tracking-widest text-[#777] mt-4">
          Sistema COOMFLONA · Almoxarifado · Documento gerado eletronicamente
        </div>
      </div>

      {/* Modal de confirmação */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !busy && setConfirmAction(null)}>
          <div className="brutal-card bg-white p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black uppercase text-lg mb-3">
              Confirmar {confirmAction === "approve" ? "aprovação" : "rejeição"}?
            </h3>
            <p className="text-sm text-[#555] mb-4">
              Você está {confirmAction === "approve" ? "aprovando" : "rejeitando"} a requisição <strong>{data.numero_sequencial || data.numero}</strong> como <strong>{nome || "—"}</strong>. Esta ação será registrada no sistema.
            </p>
            <div className="flex justify-end gap-2">
              <button disabled={busy} onClick={() => setConfirmAction(null)} className="brutal-btn bg-white text-[#111] px-4 py-2 font-display font-bold uppercase text-xs tracking-widest border-2 border-[#111]">Voltar</button>
              <button
                disabled={busy}
                onClick={() => decide(confirmAction)}
                data-testid="req-confirm-decision"
                className={`brutal-btn ${confirmAction === "approve" ? "bg-[#1A7F37]" : "bg-[#E60000]"} text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest disabled:opacity-50`}
              >
                {busy ? "Enviando..." : `Confirmar ${confirmAction === "approve" ? "aprovação" : "rejeição"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
